import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import {
  Plus,
  Package,
  CheckCircle,
  XCircle,
  ClipboardList,
  Store,
  User,
  ArrowRight,
} from "lucide-react";
import { useLang } from "@/context/LanguageContext";
import { useAuth } from "@/context/AuthContext";
import { api } from "@/lib/api";
import { ProductCard } from "@/components/ProductCard";
import { ProductGridSkeleton } from "@/components/States";
import { toNeDigits } from "@/lib/constants";

const SellerHome = () => {
  const { t, lang } = useLang();
  const { user } = useAuth();
  const navigate = useNavigate();
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    api
      .get("/my/products")
      .then((res) => setProducts(res.data))
      .finally(() => setLoading(false));
  }, []);

  const active = products.filter((p) => p.status === "active");
  const removed = products.filter((p) => p.status !== "active");
  const recent = products.slice(0, 4);

  const actions = [
    {
      testId: "supplier-home-action-add",
      icon: Plus,
      title: t("supplierHome.addProduct"),
      desc: t("supplierHome.addProductDesc"),
      onClick: () => navigate("/dashboard/add"),
    },
    {
      testId: "supplier-home-action-manage",
      icon: ClipboardList,
      title: t("supplierHome.manageListings"),
      desc: t("supplierHome.manageListingsDesc"),
      onClick: () => navigate("/dashboard"),
    },
    {
      testId: "supplier-home-action-market",
      icon: Store,
      title: t("supplierHome.viewMarket"),
      desc: t("supplierHome.viewMarketDesc"),
      onClick: () => navigate("/market"),
    },
    {
      testId: "supplier-home-action-profile",
      icon: User,
      title: t("supplierHome.myProfile"),
      desc: t("supplierHome.myProfileDesc"),
      onClick: () => navigate("/profile"),
    },
  ];

  return (
    <div>
      {/* Hero */}
      <section className="bg-[#123A2C] text-white">
        <div className="max-w-6xl mx-auto px-4 py-10 sm:py-14">
          <h1
            data-testid="supplier-home-greeting"
            className="text-2xl sm:text-3xl md:text-4xl font-extrabold tracking-tight leading-snug max-w-2xl"
          >
            {t("supplierHome.welcomeBack")}
            {user?.name ? `, ${user.name}` : ""}
          </h1>
          <p className="mt-3 text-[rgba(255,255,255,0.72)] text-sm sm:text-base max-w-xl">
            {t("supplierHome.subtitle")}
          </p>
          <button
            data-testid="supplier-home-add-btn"
            onClick={() => navigate("/dashboard/add")}
            className="mt-6 inline-flex items-center gap-2 px-5 py-2.5 rounded-full bg-[#F5A623] text-[#412402] font-semibold text-sm hover:brightness-95 transition"
          >
            <Plus className="w-4 h-4" /> {t("supplierHome.addNew")}
          </button>
        </div>
      </section>

      <div className="max-w-6xl mx-auto px-4">
        {/* Stats */}
        <div className="grid grid-cols-3 gap-3 sm:gap-6 -mt-6 relative z-10">
          {[
            {
              v: products.length,
              l: t("supplierHome.totalListings"),
              icon: Package,
            },
            {
              v: active.length,
              l: t("supplierHome.activeListings"),
              icon: CheckCircle,
            },
            {
              v: removed.length,
              l: t("supplierHome.removedListings"),
              icon: XCircle,
            },
          ].map((s, i) => {
            const Icon = s.icon;
            return (
              <div
                key={i}
                data-testid={`supplier-home-stat-${i}`}
                className="bg-white rounded-xl border border-[#E5E7EB] p-4 text-center shadow-sm flex flex-col items-center gap-1"
              >
                <Icon className="w-4 h-4 text-[#123A2C]" />
                <div className="text-xl sm:text-2xl font-extrabold text-[#123A2C]">
                  {toNeDigits(s.v, lang)}
                </div>
                <div className="text-xs text-[#6B7280]">{s.l}</div>
              </div>
            );
          })}
        </div>

        {/* Quick actions */}
        <section className="mt-8">
          <h2 className="text-xl sm:text-2xl font-bold tracking-tight text-[#14251F] mb-4">
            {t("supplierHome.quickActions")}
          </h2>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            {actions.map((a) => {
              const Icon = a.icon;
              return (
                <button
                  key={a.testId}
                  data-testid={a.testId}
                  onClick={a.onClick}
                  className="text-left rounded-2xl p-5 bg-[#F6F7F4] border border-[#E5E7EB] hover:-translate-y-0.5 hover:shadow-sm transition-all flex items-start gap-4"
                >
                  <div className="w-11 h-11 shrink-0 rounded-full bg-white flex items-center justify-center">
                    <Icon className="w-5 h-5 text-[#123A2C]" />
                  </div>
                  <div>
                    <div className="font-bold text-[#14251F]">{a.title}</div>
                    <div className="text-xs text-[#6B7280] mt-1">{a.desc}</div>
                  </div>
                </button>
              );
            })}
          </div>
        </section>

        {/* Recent listings */}
        <section className="mt-10 pb-10">
          <div className="flex items-center justify-between mb-4">
            <h2 className="text-xl sm:text-2xl font-bold tracking-tight text-[#14251F]">
              {t("supplierHome.recentListings")}
            </h2>
            {products.length > 0 && (
              <button
                data-testid="supplier-home-viewall"
                onClick={() => navigate("/dashboard")}
                className="flex items-center gap-1 text-sm font-medium text-[#123A2C] hover:gap-2 transition-all"
              >
                {t("supplierHome.viewAll")} <ArrowRight className="w-4 h-4" />
              </button>
            )}
          </div>
          {loading ? (
            <ProductGridSkeleton />
          ) : products.length === 0 ? (
            <div
              data-testid="supplier-home-empty"
              className="text-center bg-white rounded-2xl border border-dashed border-[#E5E7EB] py-12 px-4"
            >
              <p className="text-[#6B7280] mb-4">
                {t("supplierHome.noListingsTitle")}
              </p>
              <button
                data-testid="supplier-home-empty-add-btn"
                onClick={() => navigate("/dashboard/add")}
                className="inline-flex items-center gap-2 px-4 py-2.5 rounded-xl bg-[#123A2C] text-white font-medium hover:bg-[#0D2C21] transition-colors"
              >
                <Plus className="w-4 h-4" />{" "}
                {t("supplierHome.noListingsAction")}
              </button>
            </div>
          ) : (
            <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-3 sm:gap-5">
              {recent.map((p) => (
                <ProductCard key={p.id} product={p} />
              ))}
            </div>
          )}
        </section>
      </div>
    </div>
  );
};

export default SellerHome;
